/**
 * destinazione.js — da un riferimento all'indirizzo dove aprirlo.
 *
 * PERCHE' UNA FUNZIONE SOLA
 * Gli avvisi si aprono da tre posti: il riquadro in dashboard, la
 * schermata Promemoria e il popup dei compiti. Con la regola scritta tre
 * volte, il giorno che una linguetta cambia nome due schermate su tre
 * continuano a funzionare e la terza porta nel vuoto — e nessuno se ne
 * accorge, perche' un collegamento che sbaglia destinazione non da'
 * errore, mostra solo la pagina meno utile.
 *
 * IL CONFINE
 * Il database dice CHE COSA si sta indicando ('visita', 'lotto'), non
 * quale schermata la mostri. La traduzione da fatto a schermata avviene
 * qui, e qui soltanto.
 *
 * DUE FAMIGLIE, DALLA MIGRATION 115
 * I tipi clinici vivono dentro la scheda di un paziente e senza paziente
 * non hanno senso. I tipi di servizio — un lotto in scadenza, una fattura
 * scartata dal Sistema TS, un resoconto mensile — riguardano la struttura
 * e un paziente non ce l'hanno affatto. I due elenchi qui sotto devono
 * restare allineati al dominio `tipo_riferimento` nel database: se il
 * database ammette un tipo che qui non c'e', il collegamento non
 * sbaglia strada, semplicemente non compare.
 *
 * QUELLO CHE QUESTO MECCANISMO NON FA, E VA SAPUTO
 * Per i tre tipi di servizio si arriva alla SCHERMATA, non alla riga:
 * Magazzino, Sistema TS e Fatturazione non leggono lo stato della
 * navigazione, quindi non sanno aprirsi su un lotto o una fattura
 * precisa. Verificato, non supposto. Quando sapranno farlo, l'unica cosa
 * da cambiare e' questo file: il dato per arrivarci — `riferimento_id` —
 * viene gia' salvato, quindi gli avvisi scritti da oggi porteranno alla
 * riga giusta senza doverli riscrivere.
 */
import { useAuthStore } from '../store/useStore';
import { stileNuovo } from '../navigazione/paginaIniziale';

/* Tipi che stanno dentro la scheda di un paziente.
   I nomi a destra sono i valori che PazienteDetail accetta in
   `state.schermata`; quelli a sinistra vengono dal database. */
const CLINICHE = {
    visita: 'procedure',
    consensi: 'documenti',
    documento: 'documenti',
    foto: 'foto',
    fattura: 'incassi',
    anamnesi: 'profilo',
    compilazione: 'profilo',
    /* La richiesta di autorizzazione si decide DENTRO la visita, non in
       una schermata sua: si apre l'elenco delle procedure.

       Gli avvisi di autorizzazione pero' continuano a salvarsi come
       'visita' con l'id della visita, ed e' voluto: cosi' la visita si
       apre gia' espansa, che e' piu' preciso. Questo tipo serve a chi
       vuole indicare la richiesta in quanto tale. */
    richiesta_accesso: 'procedure',
};

/* Tipi che riguardano la struttura: il paziente non c'entra. */
const SERVIZIO = {
    lotto: '/magazzino',
    fattura_ts: '/sistema-ts',
    /* Una fattura scartata dallo SdI (blocco 6): porta a Fatturazione, che
       solo amministrazione e segreteria aprono - come la rotta.
       Con lo stile nuovo la schermata si apre sul mese di oggi, e uno scarto
       arrivato il primo del mese per una fattura del mese prima non ci
       sarebbe: l'indirizzo chiede il filtro «Da correggere» di tutti i mesi,
       dove lo scarto e' aperto nella riga. Lo stile di prima ignora i due
       parametri e si apre com'e' sempre. */
    fattura_sdi: '/fatturazione?filtro=da_correggere&mese=tutti',
    resoconto_ts: '/sistema-ts',
    presenze: '/area-dipendenti',
    /* Il compito «N medici con iscrizione all'albo incompleta» porta
       all'elenco, dove la colonna «Iscrizione all'albo» dice chi. */
    medici: '/medici',
    /* Una richiesta di correzione di una timbratura (339): l'avviso
       dell'amministrazione e quello della dipendente rifiutata portano alla
       stessa schermata con l'id della richiesta, e Area dipendenti si apre
       da se' sulla scheda giusta - le richieste da decidere in Presenze, o
       le proprie in «Le mie ore» - guardando di chi e' la richiesta. */
    timbratura_richiesta: '/area-dipendenti',
    /* Una busta paga, una CU o un contratto pubblicati (341): l'avviso e'
       della dipendente, e porta a «Le mie buste paga» con l'id. */
    documento_personale: '/area-dipendenti',
};

/* Un referto prodotto ha una pagina sua, e ha anche un paziente: non sta
   ne' fra i tipi di servizio ne' fra quelli che vivono dentro la scheda
   del paziente. Si risolve a parte, prima di tutti e due. */
const PERCORSO_REFERTO = '/referti/';

/* Da area del questionario a sotto-schermata del profilo medico.
   L'area la manda il server insieme all'avviso (`riferimento_area`):
   dal solo id della compilazione non si saprebbe dove aprirla. */
const SOTTOPROFILO_PER_AREA = {
    psicologico: 'alimentare',
    gastroenterologia: 'gastro',
    pelle: 'pelle',
    tricologia: 'tricologico',
    composizione: 'composizione',
    misure: 'misure',
};

/** Come si chiama la destinazione quando la si scrive in un pulsante. */
const ETICHETTA_DI_PRIMA = {
    visita: 'la visita',
    consensi: 'i consensi',
    documento: 'il documento',
    foto: 'le foto',
    fattura: 'la fattura',
    anamnesi: 'l\'anamnesi',
    compilazione: 'la scheda',
    richiesta_accesso: 'la richiesta',
    lotto: 'il magazzino',
    fattura_ts: 'il Sistema TS',
    fattura_sdi: 'la fattura in Fatturazione',
    resoconto_ts: 'il resoconto TS',
    presenze: 'le presenze',
    referto_prodotto: 'il referto',
    medici: 'l\'elenco dei medici',
    timbratura_richiesta: 'la richiesta di correzione',
    documento_personale: 'le tue buste paga',
    controllo: 'la schermata',
};

/* Dove lo stile nuovo la chiama in un altro modo (fase 5): lo scarto dello SdI
   porta alle Fatture sul filtro «Da correggere» (SERVIZIO qui sopra), e il
   pulsante dice la pagina e il filtro. Con lo stile di prima l'etichetta e'
   quella di sempre: lo decide l'utenza (navigazione/paginaIniziale). */
const ETICHETTA_CONSOLE = {
    fattura_sdi: 'le fatture da correggere',
};

/* Una sola tabella per chi legge: la voce dello stile dell'utente di adesso,
   altrimenti quella di prima. Letta al momento del disegno, come le altre. */
export const ETICHETTA = new Proxy(ETICHETTA_DI_PRIMA, {
    get(base, chiave) {
        if (typeof chiave === 'string' && ETICHETTA_CONSOLE[chiave]
            && stileNuovo(useAuthStore.getState().user)) return ETICHETTA_CONSOLE[chiave];
        return base[chiave];
    },
});

/**
 * Costruisce l'indirizzo per una voce.
 *
 * @param {object} voce  con paziente_id, riferimento_tipo, riferimento_id,
 *                       e riferimento_area per le compilazioni
 * @returns {{ percorso: string, stato: object } | null}
 */
export function destinazione(voce) {
    if (!voce) return null;

    /* Il referto prodotto ha una pagina propria, indirizzata dal suo id:
       non si passa dalla scheda del paziente, che mostrerebbe l'elenco
       invece del documento di cui parla il compito. */
    if (voce.riferimento_tipo === 'referto_prodotto' && voce.riferimento_id) {
        return { percorso: `${PERCORSO_REFERTO}${voce.riferimento_id}`, stato: {} };
    }

    /* I controlli automatici (migration 269): la schermata la dice il
       catalogo dei controlli, e il server la manda in `riferimento_area`
       (area_riferimento). Un controllo senza schermata - gli esami da
       verificare, le visite vuote: stanno nelle schede di piu' pazienti -
       non diventa un pulsante; l'elenco e' nel testo del compito. */
    if (voce.riferimento_tipo === 'controllo') {
        const a = String(voce.riferimento_area || '');
        return a.startsWith('/') ? { percorso: a, stato: {} } : null;
    }

    /* I tipi di servizio si risolvono per primi: non hanno bisogno di un
       paziente, e chiederglielo li escluderebbe tutti. */
    const percorsoServizio = SERVIZIO[voce.riferimento_tipo];
    if (percorsoServizio) {
        /* `presenze` e' il primo tipo di servizio che sa indicare la RIGA
           e non solo la schermata: porta l'id del dipendente, e Area
           dipendenti si apre sulla scheda Presenze filtrata su quella
           persona. Gli altri tre no - Magazzino, Sistema TS e
           Fatturazione non leggono lo stato della navigazione - e finche'
           e' cosi' restano com'erano.

           IL MESE viene dalla SCADENZA, che per questi compiti e'
           l'ultimo istante del mese di cui parlano. Non e' un espediente:
           e' lo stesso fatto letto una volta sola, invece di scriverlo in
           due posti che poi divergono. Se la scadenza manca - o qualcuno
           ha rinviato il compito - si apre comunque sulla persona, con il
           periodo predefinito: meno preciso, mai sbagliato. */
        if (voce.riferimento_tipo === 'presenze' && voce.riferimento_id) {
            const stato = { vista: 'presenze', dipendenteId: Number(voce.riferimento_id) };
            const mese = String(voce.scadenza || '').slice(0, 7);
            if (/^\d{4}-\d{2}$/.test(mese)) stato.mese = mese;
            return { percorso: percorsoServizio, stato };
        }
        if (voce.riferimento_tipo === 'documento_personale') {
            return { percorso: percorsoServizio,
                     stato: { vista: 'mie_buste', documentoPersonale: Number(voce.riferimento_id) || null } };
        }
        if (voce.riferimento_tipo === 'timbratura_richiesta' && voce.riferimento_id) {
            return { percorso: percorsoServizio,
                     stato: { richiestaCorrezione: Number(voce.riferimento_id) } };
        }
        return { percorso: percorsoServizio, stato: {} };
    }

    /* Da qui in poi serve il paziente: un avviso di servizio senza
       destinatario clinico non deve diventare un pulsante che non porta
       da nessuna parte. */
    if (!voce.paziente_id) return null;

    const percorso = `/pazienti/${voce.paziente_id}`;
    const schermata = CLINICHE[voce.riferimento_tipo];

    /* Riferimento assente, o di un tipo che questa versione del frontend
       non conosce ancora: si apre comunque la scheda del paziente. E' il
       comportamento di prima, che resta il ripiego giusto — meno preciso,
       mai sbagliato. */
    if (!schermata) return { percorso, stato: { schermata: 'home' } };

    const stato = { schermata };

    /* La visita si apre espansa, non solo elencata: il senso di "portami
       a quella visita" e' vederne il contenuto senza doverla ritrovare
       fra le altre sedici. */
    if (voce.riferimento_tipo === 'visita' && voce.riferimento_id) {
        stato.visitaId = Number(voce.riferimento_id);
    }

    /* La compilazione vive nella sotto-schermata della sua area. Senza
       l'area si apre l'anamnesi, che e' il ripiego del profilo: sbagliato
       come precisione, mai come destinazione. */
    if (voce.riferimento_tipo === 'compilazione') {
        const sotto = SOTTOPROFILO_PER_AREA[voce.riferimento_area];
        if (sotto) stato.sottoProfilo = sotto;
        if (voce.riferimento_id) stato.compilazioneId = Number(voce.riferimento_id);
    }

    return { percorso, stato };
}

/**
 * Scorciatoia per chi ha già un navigate() sotto mano.
 * Non fa niente se non c'e' una destinazione: meglio un clic che non
 * risponde di un salto alla pagina sbagliata.
 */
export function vaiA(navigate, voce) {
    const d = destinazione(voce);
    if (d) navigate(d.percorso, { state: d.stato });
}

/** Vero se la voce sa indicare qualcosa di piu' preciso del paziente. */
export function haDestinazione(voce) {
    if (!voce) return false;
    if (voce.riferimento_tipo === 'referto_prodotto') return Boolean(voce.riferimento_id);
    if (voce.riferimento_tipo === 'controllo') return String(voce.riferimento_area || '').startsWith('/');
    if (SERVIZIO[voce.riferimento_tipo]) return true;
    return Boolean(voce.paziente_id && CLINICHE[voce.riferimento_tipo]);
}
